import React, { useMemo } from 'react';
import styled from 'styled-components'; 
import BadgeManager from '../../../common/badges/BadgeManager.jsx';
import ButtonCircle from '../../../common/buttons/ButtonCircle.jsx';
import ProfileStatsCard from './ProfileStatsCard.jsx';
import BadgeSecondary from '../../../common/badges/BadgeSecondary.jsx';
import { newFormatKpiData } from '../../../../../utils.js';
import { useTranslation } from 'react-i18next';
import { Client } from '../../../../../interfaces/client.js';
import { TeamMember } from '../../../../../interfaces/team-member.js';
import { useAppStore } from '../../../../../stores/useAppStore';
import TextCopyable from '../../../common/TextCopyable.jsx';

interface Props {
    client: Client
    manager?: TeamMember
    secondaryManagerCount?: number
    showBtnEdit?: boolean
    showBtnTask?: boolean
    showKpis?: boolean
    showEmailDisabled?: boolean
    maxChars?: number
}

export default function CardClientProfile({
    client,
    manager = null,
    secondaryManagerCount = null,
    showBtnEdit = true,
    showBtnTask = true,
    showKpis = true,
    showEmailDisabled = true,
    maxChars = 20
}: Props) {
    const { account, instance } = useAppStore();
    const { t: gctrad } = useTranslation();

    const kpisDisplayByRh = useMemo(() => {
        return !instance?.rights.includes("gps_central_booking_right") || instance?.subdomainInstance?.kpisDisplayByRh;
    }, [instance])
    
    return (
        <Wrapper className="card border-none bg-white text-gc-dark-blue p-3">
            <div className="d-flex d-flex justify-content-between align-items-center">
                <h3 className="d-flex align-items-center h4 font-weight-bold m-0 mr-2">
                    <span className="mr-2">
                        {client?.firstName ?
                            <>{(client?.firstName + ' ' + client?.lastName).truncate(maxChars)}</>
                            :
                            client?.email.truncate(maxChars)
                        }
                    </span>
                    {((manager && manager.id) || (secondaryManagerCount > 0)) && (
                        <BadgeManager manager={manager} others_managers_count={secondaryManagerCount} />
                    )}
                </h3>
                {showBtnEdit && <ButtonCircle
                    href={'providers//clients/form/' + client.id}
                    part='+'
                    icon={<i className="fa-regular fa-pen-to-square"></i>}
                />}
            </div>

            {showKpis && account && ((client.kpis || client.kpisByRequests) && <div className='mt-3'>
            <ProfileStatsCard
                kpisLoaded={true}
                kpiItems={kpisDisplayByRh ?
                (client.kpis ? newFormatKpiData(client.kpis, false, '') : '')
                :
                (client.kpisByRequests ? newFormatKpiData(client.kpisByRequests, true, '') : '')
                }
            />
            </div>)}

            <div className="mt-3">
                <ul className="client-infos font-weight-normal text-gc-dark-blue">
                    {client.jobTitle && <li><i className="fa-regular fa-file-circle-info"></i> {client.jobTitle}</li>}
                    {client.email && <li>
                        <TextCopyable 
                            text={<span><i className="fa-regular fa-envelope"></i> {client.email}</span>}
                            value={client.email}
                        />
                    
                    {client?.emailHasError && <BadgeSecondary text={gctrad(`email_event ${client.emailHasError}`)} bgColor={'danger'} color={'white'} className={'ml-1'} />}
                    {showEmailDisabled && !(client?.accountInstance?.sendEmail) &&
                        <a
                        data-part='+'
                        href="#"
                        data-target={'providers//clients/form/' + client.id}
                        className="js-nav get_part_link ml-1"
                        >
                        <BadgeSecondary text={'disabled'.trad()} bgColor={'light'} tooltip={true} tooltipText={'email_disabled'.trad()} />
                        <i className="fa-solid fa-triangle-exclamation icones-tooltip text-danger ml-1"></i>
                        </a>
                    }
                    </li>}
                    {client.phone && <li><i className="fa-regular fa-phone"></i> {client.phone}</li>}
                    {client.address && <li>
                    <i className="fa-regular fa-location-dot"></i> {client.address
                        ? client.address + ', ' + client.postalcode + ' - ' + client.city + (client.country ? ', ' + client.country.title : '')
                        : ''}
                    </li>}
                    {client?.language?.title && <li><i className="fa-regular fa-language"></i> {client.language.title}</li>}
                </ul>
            </div>

            {showBtnTask && <div className="row mt-3">
                <div className="col-12 mb-2">
                    <a
                    data-part='+'
                    href="#"
                    data-target={'providers//tasks_controller/form_add_client/' + client.id}
                    className="btn btn-sm btn-outline-gc-dark-blue font-weight-bold w-100 js-nav get_part_link"
                    >
                    <i className="fa-regular fa-list-check mr-2"></i> {'add_task'.trad()}
                    </a>
                </div>

                <div className="col-12">
                    <a
                    data-part='+'
                    href="#"
                    data-target={'clients/request_site/' + client.id}
                    className="btn btn-sm btn-outline-gc-dark-blue font-weight-bold w-100 js-nav get_part_link"
                    >
                    {'new_request'.trad()}
                    </a>
                </div>
            </div>}
        </Wrapper>
    );
}

const Wrapper = styled.div`
    .client-infos {
        margin: 0;
        padding: 0;
        list-style: none;

        li {
            font-size: .8rem;
            margin-bottom: 0px;
        }
    }
`;